export type BannedWordInstance = { index: number; length: number; word: string };
export type TextInterval = { start: number; end: number };

/** Embed placeholders keep text offsets aligned with Quill's document offsets. */
export const getBannedWordText = (ops: { insert?: unknown }[]) =>
  ops.map((op) => typeof op.insert === "string" ? op.insert : op.insert ? "\uFFFC" : "").join("");

/** Compile once per checklist, then reuse for content edits and final validation. */
export const createBannedWordMatcher = (words: string[]) => {
  const normalized = [...new Set(words.map((word) => word.trim().toLowerCase()).filter(Boolean))]
    .sort((a, b) => b.length - a.length);
  const escaped = normalized.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = escaped.length ? new RegExp(`\\b(${escaped.join("|")})\\b`, "gi") : null;

  return (text: string, bodyRanges: TextInterval[]): BannedWordInstance[] => {
    if (!pattern) return [];
    pattern.lastIndex = 0;
    const quotes = getQuotedRanges(text);
    const matches: BannedWordInstance[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      if (bodyRanges.some((range) => match!.index >= range.start && match!.index < range.end) &&
          !quotes.some((range) => match!.index >= range.start && match!.index < range.end)) {
        matches.push({ index: match.index, length: match[0].length, word: match[0] });
      }
    }
    return matches;
  };
};

// Quoted language is exempt from the banned-word check.
const getQuotedRanges = (
  text: string
): { start: number; end: number }[] => {
  const ranges: { start: number; end: number }[] = [];
  const len = text.length;

  let inStandardQuote = false;
  let standardQuoteStart = -1;

  let smartQuoteDepth = 0;
  let smartQuoteStart = -1;

  for (let i = 0; i < len; i++) {
    const char = text[i];

    // Handle standard double quotes "
    if (char === '"') {
      if (inStandardQuote) {
        // Closing a standard quote
        ranges.push({ start: standardQuoteStart, end: i + 1 }); // +1 to include the closing quote
        inStandardQuote = false;
        standardQuoteStart = -1;
      } else {
        // Opening a standard quote
        // Only open if we aren't already deep in a smart quote (optional logic, usually independent)
        inStandardQuote = true;
        standardQuoteStart = i;
      }
    }
    // Handle smart opening quote “
    else if (char === "“") {
      if (smartQuoteDepth === 0) {
        smartQuoteStart = i;
      }
      smartQuoteDepth++;
    }
    // Handle smart closing quote ”
    else if (char === "”") {
      if (smartQuoteDepth > 0) {
        smartQuoteDepth--;
        if (smartQuoteDepth === 0) {
          ranges.push({ start: smartQuoteStart, end: i + 1 });
          smartQuoteStart = -1;
        }
      }
    }
  }

  // Optional: Handle unclosed quotes by extending to end of text
  // if (inStandardQuote) ranges.push({ start: standardQuoteStart, end: len });
  // if (smartQuoteDepth > 0) ranges.push({ start: smartQuoteStart, end: len });

  return ranges;
};

